import { Button, Group, Stack, Tabs, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState, useMemo } from 'react';
import { useGetSupportedLanguagesQuery } from '@codezo/graphql';

interface WellBeingTranslationsFormProps {
	question: any;
	onSubmit: (values: any) => void;
	onCancel: () => void;
	loading?: boolean;
}

interface QuestionTranslation {
	text: string;
}

interface TranslationFormData {
	translations: Array<{
		questionId: string;
		language: string;
		text: string;
	}>;
}

export function WellBeingTranslationsForm({
	question,
	onSubmit,
	onCancel,
	loading,
}: WellBeingTranslationsFormProps) {
	const [activeTab, setActiveTab] = useState<string>('en');

	// Fetch supported languages from backend
	const { data: languagesData } = useGetSupportedLanguagesQuery();

	// Derive languages and labels from query
	const LANGUAGES = useMemo(
		() => languagesData?.getSupportedLanguages?.map((lang) => lang.code) || ['en', 'da'],
		[languagesData]
	);

	const LANGUAGE_LABELS = useMemo(
		() =>
			languagesData?.getSupportedLanguages?.reduce(
				(acc, lang) => ({
					...acc,
					[lang.code]: lang.name,
				}),
				{} as Record<string, string>
			) || { en: 'English', da: 'Danish' },
		[languagesData]
	);

	// Transform question data to form format
	const transformedInitialValues = useMemo(() => {
		const translationsMap: Record<string, QuestionTranslation> = {};

		// Initialize with empty values for all languages
		LANGUAGES.forEach((lang) => {
			translationsMap[lang] = { text: '' };
		});

		// Fill in existing translations from the question data
		if (question.translations) {
			LANGUAGES.forEach((lang) => {
				translationsMap[lang] = { text: question.translations[lang]?.text || '' };
			});
		}

		return { translations: translationsMap };
	}, [question, LANGUAGES]);

	const form = useForm({
		initialValues: transformedInitialValues,
		validate: {
			translations: (value) => {
				const errors: any = {};
				LANGUAGES.forEach((lang) => {
					if (!value[lang]?.text?.trim()) {
						if (!errors[lang]) errors[lang] = {};
						errors[lang].text = `Text in ${LANGUAGE_LABELS[lang]} is required`;
					}
				});
				return Object.keys(errors).length > 0 ? errors : null;
			},
		},
	});

	const handleSubmit = (values: any) => {
		// Transform form data to match GraphQL input format
		const translations = LANGUAGES.map((lang) => ({
			questionId: question.id,
			language: lang,
			text: values.translations[lang].text,
		}));

		onSubmit({ translations });
	};

	return (
		<form onSubmit={form.onSubmit(handleSubmit)}>
			<Stack gap="md">
				{/* Translation Tabs */}
				<Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'en')}>
					<Tabs.List>
						{LANGUAGES.map((lang) => (
							<Tabs.Tab key={lang} value={lang}>
								{LANGUAGE_LABELS[lang]}
							</Tabs.Tab>
						))}
					</Tabs.List>

					{LANGUAGES.map((lang) => (
						<Tabs.Panel key={lang} value={lang} pt="md">
							<Stack gap="md">
								<Textarea
									label="Question Text"
									placeholder={`Question text in ${LANGUAGE_LABELS[lang]}`}
									required
									minRows={3}
									{...form.getInputProps(`translations.${lang}.text`)}
								/>
							</Stack>
						</Tabs.Panel>
					))}
				</Tabs>

				{/* Action Buttons */}
				<Group justify="flex-end" mt="md">
					<Button variant="subtle" onClick={onCancel} disabled={loading}>
						Cancel
					</Button>
					<Button type="submit" loading={loading}>
						Update Translations
					</Button>
				</Group>
			</Stack>
		</form>
	);
}
